/* ==========================================================================
   rx.js — Rolex module shared behaviours. Plain JavaScript, no dependencies.

   - <rx-header>    mobile Rolex menu (replaces routes/header.js #rolex-header branch)
   - [data-rx-back-to-top]  footer "Back to top" (replaces rolex/scrollUp.js)
   - <rx-carousel>  scroll-snap carousel (replaces sliderCarousel.js and the
                    #discover-slider branch of routes/sliders.js)
   - <rx-accordion> single-open accordion (replaces rolex/accordion.js)
   - [data-rx-event] Adobe Launch direct-call rules
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------------------
     <rx-header>
     Markup contract:
       <rx-header data-desktop-from="1750">
         <button data-rx-menu-toggle aria-expanded="false" aria-controls="ID">
         <div id="ID" data-rx-menu-panel hidden>…</div>
       </rx-header>
     Below the desktop breakpoint the panel is a toggled overlay; from the
     breakpoint up the panel is always shown and the toggle is hidden (CSS).
     ------------------------------------------------------------------------ */
  class RxHeader extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[data-rx-menu-toggle]');
      this.panel = this.querySelector('[data-rx-menu-panel]');
      if (!this.toggle || !this.panel) return;

      var from = parseInt(this.getAttribute('data-desktop-from'), 10) || 1750;
      this.desktop = window.matchMedia('(min-width: ' + from + 'px)');

      this.onToggle = this.onToggle.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onBreakpoint = this.onBreakpoint.bind(this);

      this.toggle.addEventListener('click', this.onToggle);
      this.addEventListener('keydown', this.onKeydown);
      if (this.desktop.addEventListener) {
        this.desktop.addEventListener('change', this.onBreakpoint);
      } else {
        this.desktop.addListener(this.onBreakpoint);
      }
      this.onBreakpoint();
    }

    disconnectedCallback() {
      if (!this.desktop) return;
      if (this.desktop.removeEventListener) {
        this.desktop.removeEventListener('change', this.onBreakpoint);
      } else {
        this.desktop.removeListener(this.onBreakpoint);
      }
    }

    isOpen() {
      return this.toggle.getAttribute('aria-expanded') === 'true';
    }

    open() {
      this.toggle.setAttribute('aria-expanded', 'true');
      this.panel.hidden = false;
      this.classList.add('rx-is-open');
    }

    close(returnFocus) {
      this.toggle.setAttribute('aria-expanded', 'false');
      this.classList.remove('rx-is-open');
      if (!this.desktop.matches) this.panel.hidden = true;
      if (returnFocus) this.toggle.focus();
    }

    onToggle() {
      if (this.isOpen()) {
        this.close(false);
      } else {
        this.open();
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.isOpen() && !this.desktop.matches) {
        event.preventDefault();
        this.close(true);
      }
    }

    onBreakpoint() {
      if (this.desktop.matches) {
        // Desktop: inline menu, always visible.
        this.toggle.setAttribute('aria-expanded', 'false');
        this.classList.remove('rx-is-open');
        this.panel.hidden = false;
      } else {
        this.close(false);
      }
    }
  }

  /* ------------------------------------------------------------------------
     Back to top (footer)
     ------------------------------------------------------------------------ */
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-rx-back-to-top]');
    if (!trigger) return;
    event.preventDefault();
    window.scrollTo({ top: 0, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    // Move focus to the top of the document so keyboard users land there too.
    var body = document.body;
    var hadTabindex = body.hasAttribute('tabindex');
    if (!hadTabindex) body.setAttribute('tabindex', '-1');
    body.focus({ preventScroll: true });
    if (!hadTabindex) {
      body.addEventListener('blur', function () { body.removeAttribute('tabindex'); }, { once: true });
    }
  });

  /* ------------------------------------------------------------------------
     <rx-carousel>
     Markup contract:
       <rx-carousel [data-per-view="1"] [data-loop] [data-autoplay="3000"]
                    aria-roledescription="carousel" aria-label="…">
         <div class="rx-carousel__track" data-rx-track tabindex="0">
           <div class="rx-carousel__slide" data-rx-slide>…</div> …
         </div>
         <div class="rx-carousel__nav rx-carousel__nav--prev"><button data-rx-prev …></div>
         <div class="rx-carousel__nav rx-carousel__nav--next"><button data-rx-next …></div>
         <div class="rx-carousel__bullets" data-rx-bullets></div>
       </rx-carousel>

     Slides per view come from the CSS custom property --rx-per-view (2 / 3 / 4
     at 0 / 714 / 1025px, like sliderCarousel.js), so layout and JS share one
     source. It moves one slide per step. Bullets = one per reachable start
     position (slides - perView + 1). Prev and next disable at the ends unless
     data-loop is set.

     Opt-in from CSS (both off unless a section's own CSS sets them):
       --rx-paged: 1        arrows / keys move one page (perView slides), one
                            bullet per page, the last page ends on the last
                            slide (17 / 4 = pages at 0, 4, 8, 12, 13); page-start
                            slides get [data-rx-page-start] for scroll snapping.
                            Resize re-pages after 150ms (debounced).
     The bullet wrapper gets --rx-bullet-count (for CSS that sizes bullets).
     ------------------------------------------------------------------------ */
  class RxCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-rx-track]');
      if (!this.track) return;
      this.slides = Array.prototype.slice.call(this.track.querySelectorAll('[data-rx-slide]'));
      this.prevBtn = this.querySelector('[data-rx-prev]');
      this.nextBtn = this.querySelector('[data-rx-next]');
      this.bulletWrap = this.querySelector('[data-rx-bullets]');
      this.loop = this.hasAttribute('data-loop');
      this.index = 0;

      this.slides.forEach(function (slide, i, all) {
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', 'slide');
        if (!slide.hasAttribute('aria-label')) {
          slide.setAttribute('aria-label', (i + 1) + ' / ' + all.length);
        }
      });

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.layout = this.layout.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      if (this.prevBtn) this.prevBtn.addEventListener('click', this.prev.bind(this));
      if (this.nextBtn) this.nextBtn.addEventListener('click', this.next.bind(this));
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('keydown', this.onKeydown);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', this.onResize);
      }

      this.setupAutoplay();
      this.onResize();
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      window.removeEventListener('resize', this.onResize);
      window.clearTimeout(this.resizeTimer);
      this.stopAutoplay();
    }

    perView() {
      var value = parseInt(getComputedStyle(this).getPropertyValue('--rx-per-view'), 10);
      return value > 0 ? value : 1;
    }

    cssNumber(name) {
      return parseInt(getComputedStyle(this).getPropertyValue(name), 10);
    }

    // Start index of every bullet: each position, or (paged) each page.
    starts() {
      var max = this.maxIndex();
      var starts = [];
      var step = this.paged ? this.perView() : 1;
      for (var i = 0; i < max; i += step) starts.push(i);
      starts.push(max);
      return starts;
    }

    // Bullet (page) that the current index belongs to.
    position() {
      var starts = this.starts();
      var pos = 0;
      while (pos + 1 < starts.length && starts[pos + 1] <= this.index) pos++;
      return pos;
    }

    maxIndex() {
      return Math.max(0, this.slides.length - this.perView());
    }

    slideWidth() {
      return this.slides.length ? this.slides[0].getBoundingClientRect().width : 0;
    }

    goTo(index, instant) {
      var max = this.maxIndex();
      if (this.loop) {
        if (index > max) index = 0;
        if (index < 0) index = max;
      } else {
        index = Math.min(Math.max(index, 0), max);
      }
      this.index = index;
      var left = index * this.slideWidth();
      var behavior = instant || reduceMotion.matches ? 'auto' : 'smooth';
      if (this.track.scrollTo) {
        this.track.scrollTo({ left: left, behavior: behavior });
      } else {
        this.track.scrollLeft = left;
      }
      this.update();
    }

    prev() {
      if (!this.paged) return this.goTo(this.index - 1);
      var starts = this.starts();
      var pos = this.position();
      var target = starts[this.index > starts[pos] ? pos : pos - 1];
      this.goTo(target === undefined ? -1 : target); // -1: loop or clamp as goTo does
    }

    next() {
      if (!this.paged) return this.goTo(this.index + 1);
      var starts = this.starts();
      var target = starts[this.position() + 1];
      this.goTo(target === undefined ? this.maxIndex() + 1 : target);
    }

    onScroll() {
      // Keep index in sync with native (touch or trackpad) scrolling.
      window.cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = window.requestAnimationFrame(function () {
        var width = this.slideWidth();
        if (!width) return;
        var index = Math.round(this.track.scrollLeft / width);
        var max = this.maxIndex();
        // At the scroll end, snap the index to max even when rounding lands short.
        if (this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2) index = max;
        index = Math.min(Math.max(index, 0), max);
        if (index !== this.index) {
          this.index = index;
          this.update();
        }
      }.bind(this));
    }

    onKeydown(event) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.next();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.prev();
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.goTo(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        this.goTo(this.maxIndex());
      }
    }

    onResize() {
      // Paged carousels re-page once resizing settles; the others as before.
      if (this.laidOut && (this.paged || this.cssNumber('--rx-paged') === 1)) {
        window.clearTimeout(this.resizeTimer);
        this.resizeTimer = window.setTimeout(this.layout, 150);
        return;
      }
      this.layout();
    }

    layout() {
      this.laidOut = true;
      this.paged = this.cssNumber('--rx-paged') === 1;
      this.buildBullets();
      if (this.index > this.maxIndex()) this.index = this.maxIndex();
      if (this.paged) this.index = this.starts()[this.position()];
      var starts = this.paged ? this.starts() : [];
      this.slides.forEach(function (slide, i) {
        slide.toggleAttribute('data-rx-page-start', starts.indexOf(i) !== -1);
      });
      this.goTo(this.index, true);
    }

    buildBullets() {
      if (!this.bulletWrap) return;
      var count = this.starts().length;
      var key = (this.paged ? 'page' : 'slide') + count;
      if (this.bulletKey === key) return;
      this.bulletKey = key;
      this.bulletCount = count;
      this.bulletWrap.innerHTML = '';
      this.bulletWrap.hidden = count < 2;
      this.bulletWrap.style.setProperty('--rx-bullet-count', String(count));
      var label = this.getAttribute('data-bullet-label') || 'Go to slide';
      var starts = this.starts();
      for (var i = 0; i < count; i++) {
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'rx-carousel__bullet';
        button.setAttribute('aria-label', this.paged ? 'Go to page ' + (i + 1) + ' of ' + count : label + ' ' + (i + 1));
        button.appendChild(document.createElement('span'));
        button.addEventListener('click', this.goTo.bind(this, starts[i], false));
        this.bulletWrap.appendChild(button);
      }
    }

    update() {
      var max = this.maxIndex();
      if (this.prevBtn) this.prevBtn.disabled = !this.loop && this.index <= 0;
      if (this.nextBtn) this.nextBtn.disabled = !this.loop && this.index >= max;
      var perView = this.perView();
      if (this.bulletWrap) {
        var current = this.position();
        var bullets = this.bulletWrap.children;
        for (var i = 0; i < bullets.length; i++) {
          bullets[i].setAttribute('aria-current', i === current ? 'true' : 'false');
        }
      }
      // Hide slides that are fully out of view from assistive tech and tab order.
      this.slides.forEach(function (slide, i) {
        var visible = i >= this.index && i < this.index + perView;
        slide.toggleAttribute('inert', !visible);
      }, this);
    }

    setupAutoplay() {
      var delay = parseInt(this.getAttribute('data-autoplay'), 10);
      if (!delay || reduceMotion.matches || this.slides.length < 2) return;
      this.autoplayDelay = delay;
      var stop = this.stopAutoplay.bind(this);
      var start = this.startAutoplay.bind(this);
      this.addEventListener('mouseenter', stop);
      this.addEventListener('mouseleave', start);
      this.addEventListener('focusin', stop);
      this.addEventListener('focusout', start);
      this.startAutoplay();
    }

    startAutoplay() {
      if (!this.autoplayDelay) return;
      this.stopAutoplay();
      this.autoplayTimer = window.setInterval(function () {
        if (!this.loop && this.index >= this.maxIndex()) {
          this.goTo(0);
        } else {
          this.next();
        }
      }.bind(this), this.autoplayDelay);
    }

    stopAutoplay() {
      window.clearInterval(this.autoplayTimer);
    }
  }

  /* ------------------------------------------------------------------------
     <rx-accordion> (replaces scripts/rolex/accordion.js)
     Buttons [data-rx-accordion-trigger] with aria-expanded / aria-controls;
     panels use the hidden attribute. With data-single, opening one item
     closes the others (WP behaviour). The initial open item is rendered
     server-side, so the markup works before this script runs.
     ------------------------------------------------------------------------ */
  class RxAccordion extends HTMLElement {
    connectedCallback() {
      this.single = this.hasAttribute('data-single');
      this.onClick = this.onClick.bind(this);
      this.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    triggers() {
      return Array.prototype.slice.call(this.querySelectorAll('[data-rx-accordion-trigger]'));
    }

    setOpen(trigger, open) {
      var panel = document.getElementById(trigger.getAttribute('aria-controls'));
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (panel) panel.hidden = !open;
    }

    onClick(event) {
      var trigger = event.target.closest('[data-rx-accordion-trigger]');
      if (!trigger || !this.contains(trigger)) return;
      var open = trigger.getAttribute('aria-expanded') !== 'true';
      if (open && this.single) {
        this.triggers().forEach(function (other) {
          if (other !== trigger) this.setOpen(other, false);
        }, this);
      }
      this.setOpen(trigger, open);
    }
  }

  /* ------------------------------------------------------------------------
     Adobe Launch direct-call rules (WP: onclick="_satellite.track('…')")
     Any element with data-rx-event="contactCall" etc. (not data-rx-track,
     which is the carousel track). Does nothing unless Launch has loaded
     (tracking enabled + consent).
     ------------------------------------------------------------------------ */
  document.addEventListener('click', function (event) {
    var el = event.target.closest('[data-rx-event]');
    if (!el) return;
    var name = el.getAttribute('data-rx-event');
    if (name && window._satellite && typeof window._satellite.track === 'function') {
      try { window._satellite.track(name); } catch (e) { /* ignore */ }
    }
  });

  if (!customElements.get('rx-header')) customElements.define('rx-header', RxHeader);
  if (!customElements.get('rx-accordion')) customElements.define('rx-accordion', RxAccordion);
  if (!customElements.get('rx-carousel')) customElements.define('rx-carousel', RxCarousel);
})();
